import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Loader2, PackageCheck, Truck, CheckCircle2, ChevronRight, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { sendOrQueue } from "@/lib/outbox";
import { useAuth } from "@/hooks/useAuth";

type SellerConfirmScheduleBanner = {
  kind: "seller_confirm_schedule";
  role: "seller";
  orderId: string;
  orderRef: string;
  selectionId: string;
  sellerName: string;
  buyerName: string;
  scheduledFor: string;
  scheduledUntil: string;
  pickupLocation: string;
  driverName: string | null;
};

type SellerMarkReadyBanner = {
  kind: "seller_mark_ready";
  role: "seller";
  orderId: string;
  orderRef: string;
  selectionId: string;
  sellerName: string;
  buyerName: string;
  scheduledFor: string;
  scheduledUntil: string;
  pickupLocation: string;
  driverName: string | null;
};

type DriverAcknowledgePickupBanner = {
  kind: "driver_acknowledge_pickup";
  role: "driver";
  orderId: string;
  orderRef: string;
  deliveryRequestId: string;
  sellerName: string;
  buyerName: string;
  pickupLocation: string;
  readyAt: string;
};

export type HandshakeBanner =
  | SellerConfirmScheduleBanner
  | SellerMarkReadyBanner
  | DriverAcknowledgePickupBanner;

function formatWindow(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const day = s.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
  const startTime = s.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const endTime = e.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${day}, ${startTime}–${endTime}`;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "just now";
  const mins = Math.round(diff / 60_000);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function HandshakeBanners() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const { isAuthenticated } = useAuth();

  const { data, isLoading } = useQuery<{ banners: HandshakeBanner[] }>({
    queryKey: ["/api/my/handshakes"],
    enabled: isAuthenticated,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const banners = data?.banners ?? [];

  const confirmSchedule = useMutation({
    mutationFn: (b: SellerConfirmScheduleBanner) =>
      sendOrQueue({
        kind: "confirm-schedule",
        label: "Confirm pickup time",
        url: `/api/orders/${b.orderId}/seller/confirm-schedule`,
        method: "POST",
        body: { selectionId: b.selectionId },
      }),
    onSuccess: (result) => {
      if (result === "queued") {
        toast({ title: "Saved offline", description: "We'll confirm the pickup time when you're back in service." });
        return;
      }
      toast({ title: "Pickup time confirmed" });
      queryClient.invalidateQueries({ queryKey: ["/api/my/handshakes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/seller/orders"] });
    },
    onError: (err: Error) =>
      toast({ title: "Couldn't confirm", description: err.message, variant: "destructive" }),
  });

  const markReady = useMutation({
    mutationFn: (b: SellerMarkReadyBanner) =>
      sendOrQueue({
        kind: "mark-ready",
        label: "Mark order ready",
        url: `/api/orders/${b.orderId}/seller/mark-ready`,
        method: "POST",
        body: { selectionId: b.selectionId },
      }),
    onSuccess: (result) => {
      if (result === "queued") {
        toast({ title: "Saved offline", description: "The driver will be notified as soon as you're back in service." });
        return;
      }
      toast({ title: "Marked ready", description: "Driver has been notified." });
      queryClient.invalidateQueries({ queryKey: ["/api/my/handshakes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/seller/orders"] });
    },
    onError: (err: Error) =>
      toast({ title: "Couldn't mark ready", description: err.message, variant: "destructive" }),
  });

  const acknowledge = useMutation({
    mutationFn: (b: DriverAcknowledgePickupBanner) =>
      sendOrQueue({
        kind: "acknowledge-pickup",
        label: "Acknowledge pickup",
        url: `/api/delivery-requests/${b.deliveryRequestId}/acknowledge-pickup`,
        method: "POST",
        body: {},
      }),
    onSuccess: (result) => {
      if (result === "queued") {
        toast({ title: "Saved offline", description: "This will send when you're back in service — head to pickup." });
        return;
      }
      toast({ title: "Acknowledged — head to pickup" });
      queryClient.invalidateQueries({ queryKey: ["/api/my/handshakes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/my/deliveries"] });
    },
    onError: (err: Error) =>
      toast({ title: "Couldn't acknowledge", description: err.message, variant: "destructive" }),
  });

  // Sort: drivers' acknowledge first (most time-sensitive), then
  // sellers' day-of "mark ready", then sellers' schedule confirmations.
  const ordered = useMemo(() => {
    const order = { driver_acknowledge_pickup: 0, seller_mark_ready: 1, seller_confirm_schedule: 2 } as const;
    return [...banners].sort((a, b) => order[a.kind] - order[b.kind]);
  }, [banners]);

  if (!isAuthenticated || isLoading || ordered.length === 0) return null;

  return (
    <div className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur">
      <div className="container mx-auto max-w-3xl space-y-2 px-3 py-2">
        {ordered.map((banner) => {
          if (banner.kind === "seller_confirm_schedule") {
            return (
              <div
                key={`sched-${banner.selectionId}`}
                className="rounded-xl border-2 border-primary/40 bg-primary/5 p-3 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold">
                      Confirm pickup time for #{banner.orderRef}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {banner.driverName ?? "A driver"} scheduled {formatWindow(banner.scheduledFor, banner.scheduledUntil)} for {banner.buyerName}'s order. Have items packaged at <span className="font-medium text-foreground">{banner.pickupLocation || "your pickup location"}</span> by then.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => confirmSchedule.mutate(banner)}
                  disabled={confirmSchedule.isPending}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
                >
                  {confirmSchedule.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4" />
                  )}
                  This time works for me
                </button>
              </div>
            );
          }

          if (banner.kind === "seller_mark_ready") {
            return (
              <div
                key={`ready-${banner.selectionId}`}
                className="rounded-xl border-2 border-amber-400/60 bg-amber-50 p-3 shadow-sm dark:bg-amber-950/30"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-200 dark:bg-amber-900/40">
                    <PackageCheck className="h-4 w-4 text-amber-900 dark:text-amber-200" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold">
                      Pickup today: #{banner.orderRef}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {banner.driverName ?? "A driver"} arrives {formatWindow(banner.scheduledFor, banner.scheduledUntil)}. Pack the items, label them with the buyer's name ({banner.buyerName}), and place at <span className="font-medium text-foreground">{banner.pickupLocation || "your pickup location"}</span>. Confirm when ready.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => markReady.mutate(banner)}
                  disabled={markReady.isPending}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-md bg-amber-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-amber-700 disabled:opacity-60"
                >
                  {markReady.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <PackageCheck className="h-4 w-4" />
                  )}
                  Order is ready for pickup
                </button>
              </div>
            );
          }

          // driver_acknowledge_pickup
          return (
            <div
              key={`ack-${banner.deliveryRequestId}`}
              className="rounded-xl border-2 border-emerald-500/60 bg-emerald-50 p-3 shadow-sm dark:bg-emerald-950/30"
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-200 dark:bg-emerald-900/40">
                  <Truck className="h-4 w-4 text-emerald-900 dark:text-emerald-200" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">
                    Pickup ready: #{banner.orderRef}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{banner.sellerName}</span> marked the order ready {relativeTime(banner.readyAt)}. Pickup at {banner.pickupLocation || "the seller"}. Acknowledge before heading out.
                  </p>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => acknowledge.mutate(banner)}
                  disabled={acknowledge.isPending}
                  className="flex items-center justify-center gap-2 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:opacity-60"
                >
                  {acknowledge.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4" />
                  )}
                  On my way
                </button>
                <button
                  type="button"
                  onClick={() => setLocation("/deliveries")}
                  className="flex items-center justify-center gap-1 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium transition hover:bg-accent"
                >
                  Open
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}

        {ordered.length > 1 && (
          <div className="flex items-center gap-1 px-1 text-[11px] text-muted-foreground">
            <AlertCircle className="h-3 w-3" />
            {ordered.length} actions waiting
          </div>
        )}
      </div>
    </div>
  );
}
